import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { LicensePlan } from '../types/license';
import { licenseService } from '../services/licenseService';

export type NMonthModalMode = 'add' | 'upgrade';
export type PurchaseMode = 'single' | 'group';

export type NMonthOneYearIntent = 'purchase' | 'extension' | 'upgrade';

const MIN_MONTHS = 1;
const MAX_MONTHS = 11;

interface NMonthPlanModalProps {
  isOpen: boolean;
  plan: LicensePlan | null;
  mode: NMonthModalMode;
  upgradePrice: number | null;
  upgradeCalculationLines?: string[];
  planTypeName: string;
  onClose: () => void;
  onConfirmAdd: (months: number) => void;
  onConfirmUpgrade: () => void;
  purchaseMode?: PurchaseMode;
  onConfirmGroupPurchase?: (payload: { months: number; quantity: number; groupName: string; targetEmail: string }) => void;
  /** 1년 이용권 구입 모드: 가격, 유형, 확인 콜백 */
  oneYearPrice?: number;
  oneYearIntent?: NMonthOneYearIntent;
  onConfirmOneYear?: () => void;
}

const NMonthPlanModal: React.FC<NMonthPlanModalProps> = ({
  isOpen,
  plan,
  mode,
  upgradePrice,
  upgradeCalculationLines = [],
  planTypeName,
  onClose,
  onConfirmAdd,
  onConfirmUpgrade,
  oneYearPrice,
  oneYearIntent,
  onConfirmOneYear,
  purchaseMode = 'single',
  onConfirmGroupPurchase,
}) => {
  const [months, setMonths] = useState(1);
  const [inputValue, setInputValue] = useState('1');
  const [agreeImmediateActivation, setAgreeImmediateActivation] = useState(false);
  const [agreeRefundPolicy, setAgreeRefundPolicy] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [targetEmail, setTargetEmail] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [quantityInputValue, setQuantityInputValue] = useState('1');

  useEffect(() => {
    if (isOpen) {
      setMonths(1);
      setInputValue('1');
      setAgreeImmediateActivation(false);
      setAgreeRefundPolicy(false);
      setGroupName('');
      setTargetEmail('');
      setQuantity(1);
      setQuantityInputValue('1');
    }
  }, [isOpen]);

  if (!isOpen) return null;
  if (!plan || plan.planType === 'free') return null;

  const isOneYearMode = plan.duration === 12 && onConfirmOneYear != null;
  const clamp = (n: number) => Math.max(MIN_MONTHS, Math.min(MAX_MONTHS, n));
  const clampQuantity = (n: number) => Math.max(1, Math.min(200, n));
  const isGroupOneYear = purchaseMode === 'group' && plan.duration === 12;
  const effectiveMonths = isGroupOneYear ? 12 : (inputValue === '' ? 1 : months);
  const effectiveQuantity = quantityInputValue === '' ? 1 : quantity;
  const monthlyRate = plan.price;
  const unitAmount = (isOneYearMode || isGroupOneYear)
    ? (oneYearPrice ?? plan.price)
    : (mode === 'add' ? monthlyRate * effectiveMonths : (upgradePrice ?? 0));
  const totalAmount = purchaseMode === 'group' ? unitAmount * effectiveQuantity : unitAmount;
  const canSubmitAdd = mode === 'add' && (isGroupOneYear || (effectiveMonths >= MIN_MONTHS && effectiveMonths <= MAX_MONTHS));
  const canSubmitUpgrade = mode === 'upgrade' && upgradePrice != null && upgradePrice > 0;
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(targetEmail.trim());
  const isGroupFormValid = purchaseMode !== 'group' || (groupName.trim().length > 0 && isValidEmail && effectiveQuantity >= 1);
  const agreeAll = agreeImmediateActivation && agreeRefundPolicy;
  const canSubmitOneYear = isOneYearMode && oneYearPrice != null && oneYearPrice >= 0 && agreeAll;
  const canSubmit = (isOneYearMode ? canSubmitOneYear : (mode === 'add' ? canSubmitAdd : canSubmitUpgrade) && agreeAll) && isGroupFormValid;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      setInputValue('');
      setMonths(1);
      return;
    }
    if (/^\d+$/.test(raw)) {
      const num = parseInt(raw, 10);
      setInputValue(raw);
      setMonths(clamp(num));
    }
  };

  const handleDecrement = () => {
    const next = clamp(months - 1);
    setMonths(next);
    setInputValue(String(next));
  };
  const handleIncrement = () => {
    const next = clamp(months + 1);
    setMonths(next);
    setInputValue(String(next));
  };

  const handleBlur = () => {
    if (inputValue === '') {
      setInputValue('1');
      setMonths(1);
    } else {
      const n = clamp(parseInt(inputValue, 10) || 1);
      setMonths(n);
      setInputValue(String(n));
    }
  };

  const handleQuantityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      setQuantityInputValue('');
      setQuantity(1);
      return;
    }
    if (/^\d+$/.test(raw)) {
      const num = parseInt(raw, 10);
      setQuantityInputValue(raw);
      setQuantity(clampQuantity(num));
    }
  };

  const handleQuantityBlur = () => {
    if (quantityInputValue === '') {
      setQuantityInputValue('1');
      setQuantity(1);
    } else {
      const n = clampQuantity(parseInt(quantityInputValue, 10) || 1);
      setQuantity(n);
      setQuantityInputValue(String(n));
    }
  };

  const handleQuantityDecrement = () => {
    const next = clampQuantity(quantity - 1);
    setQuantity(next);
    setQuantityInputValue(String(next));
  };

  const handleQuantityIncrement = () => {
    const next = clampQuantity(quantity + 1);
    setQuantity(next);
    setQuantityInputValue(String(next));
  };

  const handleSubmit = () => {
    if (purchaseMode === 'group' && onConfirmGroupPurchase) {
      onConfirmGroupPurchase({
        months: (isOneYearMode || isGroupOneYear) ? 12 : (inputValue === '' ? 1 : clamp(months)),
        quantity: effectiveQuantity,
        groupName: groupName.trim(),
        targetEmail: targetEmail.trim(),
      });
      return;
    }
    if (isOneYearMode && canSubmitOneYear && onConfirmOneYear) {
      onConfirmOneYear();
      return;
    }
    const submitMonths = inputValue === '' ? 1 : clamp(months);
    if (mode === 'add' && canSubmitAdd) {
      onConfirmAdd(submitMonths);
    } else if (mode === 'upgrade' && canSubmitUpgrade) {
      onConfirmUpgrade();
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Content onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{planTypeName} · 이용권 구입</Title>
          <CloseButton type="button" onClick={onClose} aria-label="닫기">×</CloseButton>
        </Header>
        <Body>
          {purchaseMode === 'group' && (
            <>
              <SectionLabelCentered>그룹명</SectionLabelCentered>
              <CenteredTextInput
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="예: 2026 봄학기 3학년"
                maxLength={60}
              />
              <SectionLabelCentered>쿠폰 수신 이메일</SectionLabelCentered>
              <CenteredTextInput
                type="email"
                value={targetEmail}
                onChange={(e) => setTargetEmail(e.target.value)}
                placeholder="example@domain.com"
              />
              <SectionLabel>이용권 수</SectionLabel>
              <StepperRow>
                <StepperButton type="button" onClick={handleQuantityDecrement} disabled={effectiveQuantity <= 1} aria-label="이용권 수 감소">
                  -
                </StepperButton>
                <StepperInput
                  type="text"
                  inputMode="numeric"
                  value={quantityInputValue}
                  onChange={handleQuantityInputChange}
                  onBlur={handleQuantityBlur}
                  onFocus={(e) => e.target.select()}
                />
                <StepperButton type="button" onClick={handleQuantityIncrement} aria-label="이용권 수 증가">
                  +
                </StepperButton>
              </StepperRow>
            </>
          )}
          {isOneYearMode ? (
            <>
              <PaymentLabel>결제금액</PaymentLabel>
              <PaymentAmount>{licenseService.formatPrice(totalAmount, plan.currency)}</PaymentAmount>
              {oneYearIntent === 'extension' && (
                <DetailText>1년 기간 확장 (할인가 기준)</DetailText>
              )}
              {oneYearIntent === 'upgrade' && (
                <DetailText>1년 이용권 업그레이드</DetailText>
              )}
            </>
          ) : mode === 'upgrade' ? (
            <>
              <UpgradeCostLabel>업그레이드 비용</UpgradeCostLabel>
              <UpgradeCostAmount>
                {upgradePrice != null ? `${upgradePrice.toLocaleString()}원` : '-'}
              </UpgradeCostAmount>
              {upgradeCalculationLines.length > 0 ? (
                <UpgradeCalculationBlock>
                  {upgradeCalculationLines.map((line, i) => (
                    <UpgradeCalculationLine key={i}>{line}</UpgradeCalculationLine>
                  ))}
                </UpgradeCalculationBlock>
              ) : null}
            </>
          ) : (
            <>
              {!isGroupOneYear && (
                <>
                  <SectionLabel>추가할 이용권 개월 수</SectionLabel>
                  <StepperRow>
                    <StepperButton type="button" onClick={handleDecrement} disabled={months <= MIN_MONTHS} aria-label="감소">
                      -
                    </StepperButton>
                    <StepperInput
                      type="text"
                      inputMode="numeric"
                      value={inputValue}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      onFocus={(e) => e.target.select()}
                    />
                    <StepperButton type="button" onClick={handleIncrement} disabled={months >= MAX_MONTHS} aria-label="증가">
                      +
                    </StepperButton>
                  </StepperRow>
                </>
              )}

              <PaymentLabel>결제금액</PaymentLabel>
              <PaymentAmount>{licenseService.formatPrice(totalAmount, plan.currency)}</PaymentAmount>
              <DetailText>
                월 {monthlyRate.toLocaleString()}원 × {effectiveMonths}개월
                {purchaseMode === 'group' ? ` × ${effectiveQuantity}개` : ''}
              </DetailText>
            </>
          )}

          <AgreementBlock>
            <AgreementLabel>
              <input
                type="checkbox"
                checked={agreeImmediateActivation}
                onChange={(e) => setAgreeImmediateActivation(e.target.checked)}
                id="nmonth-agree-activation"
              />
              <span>
                {purchaseMode === 'group'
                  ? '그룹 이용권의 활성화 기간은 구입 시점으로 부터 한 달 입니다. 한 달안에 구입한 이용권을 모두 등록해주세요.'
                  : '이용권 구매 즉시 활성화됩니다.'}
              </span>
            </AgreementLabel>
            <AgreementLabel>
              <input
                type="checkbox"
                checked={agreeRefundPolicy}
                onChange={(e) => setAgreeRefundPolicy(e.target.checked)}
                id="nmonth-agree-refund"
              />
              <span>
                {purchaseMode === 'group'
                  ? '그룹 이용권 환불은 구매 후 7일 이내 가능하며 환불 시 비활성화 이용권은 100% 전액 환불되고 활성화 이용권의 경우는 단일 이용권 금액의 취소 수수료 10% 및 1일 금액 × 사용일수(활성화일 포함) 만큼 제외한 금액이 환불됩니다.'
                  : '환불은 구매 후 7일 이내 가능하며 환불 시 취소 수수료 10% 및 이용권의 1일 금액 × 사용일수(구매일 포함) 만큼 제외한 금액이 환불됩니다.'}
              </span>
            </AgreementLabel>
          </AgreementBlock>
        </Body>
        <Footer>
          <CancelButton type="button" onClick={onClose}>취소</CancelButton>
          <SubmitButton
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            결제하기
          </SubmitButton>
        </Footer>
      </Content>
    </Overlay>
  );
};

export default NMonthPlanModal;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 10002;
  padding: 48px 20px 80px;
  overflow-y: auto;
`;

const Content = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: var(--color-text-secondary);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  &:hover { color: var(--color-text-primary); }
`;

const Body = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const SectionLabelCentered = styled(SectionLabel)`
  text-align: center;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 14px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text-primary);
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }
`;

const CenteredTextInput = styled(TextInput)`
  text-align: center;
`;

const StepperRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const StepperButton = styled.button`
  width: 44px;
  height: 44px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: 22px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  &:hover:not(:disabled) {
    background: var(--color-border);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StepperInput = styled.input`
  width: 80px;
  padding: 12px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text-primary);
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }
`;

const PaymentLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-align: center;
  margin-top: 8px;
`;

const PaymentAmount = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--color-accent-strong);
  text-align: center;
  :root:not([data-theme='light']) & {
    color: white;
  }
`;

const DetailText = styled.div`
  font-size: 13px;
  color: var(--color-text-secondary);
  text-align: center;
`;

const UpgradeCostLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-align: center;
  margin-top: 4px;
`;

const UpgradeCostAmount = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--color-accent-strong);
  text-align: center;
  margin: 8px 0 4px;
  :root:not([data-theme='light']) & {
    color: white;
  }
`;

const UpgradeCalculationBlock = styled.div`
  margin-top: 8px;
  padding: 10px 12px;
  background: var(--color-background);
  border-radius: 8px;
  border: 1px solid var(--color-border);
`;

const UpgradeCalculationLine = styled.div`
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.5;
  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;

const AgreementBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
`;

const AgreementLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.5;
  cursor: pointer;
  user-select: none;
  text-align: left;
  & input[type='checkbox'] {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 2px;
    accent-color: var(--color-accent-strong);
  }
  & span {
    flex: 1;
    text-align: left;
  }
`;

const Footer = styled.div`
  display: flex;
  width: 100%;
  border-top: 1px solid var(--color-border);
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 16px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-right: 1px solid var(--color-border);
  border-radius: 0 0 0 12px;
  background: var(--color-background);
  color: var(--color-text-primary);
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const SubmitButton = styled.button`
  flex: 1;
  padding: 16px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 0 0 12px 0;
  background: var(--color-accent-strong);
  color: white;
  cursor: pointer;
  &:hover:not(:disabled) { opacity: 0.95; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
